import React, { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { capturePosthogEvent, posthogEvents } from '../../utils/posthog';
import 'react-phone-input-2/lib/style.css';

import s from './index.module.css';

import metaActions from '../../store/actions/meta';

import CodeInput from 'react-code-input';
import PhoneInput from 'react-phone-input-2';
import Modal from '../Modal';
import Button from '../Button';

import {
    codeInputStyles,
    inputStyles,
    dropdownStyles,
    PLUS_SIGN,
    DEFAULT_TIME_TO_NEXT_CALL,
    TIME_BEFORE_SMS_AVAILABLE,
    CALL_AUTH_TYPE,
    CODE_INPUT_FIELDS_AMOUNT,
    COUNTRIES_LIST,
    COUNTRIES_PARAMS,
    SMS_AUTH_TYPE,
    RECAPTCHA_SITE_KEY,
} from './constants';
import { getIsAuthCodeValid, getIsAuthModalOpened, getIsPhoneAuthRequestFailed, getAuthCodeErrorMessage } from '../../store/selectors';
import {
    getRecaptchaResponse,
    renderRecaptchaWidget,
    resetRecaptchaWidget,
} from '../../utils/recaptcha';

const modalHeaderTextOptions = {
    firstStep: 'Войдите или зарегистрируйтесь, чтобы продолжить',
    secondStepCall: 'Введите последние 4 цифры входящего номера',
    secondStepSms: 'Введите код из смс',
};

const modalDescriptionOptions = {
    call: 'На ваш номер сейчас поступит звонок. Отвечать на него не нужно. Введите последние 4 цифры входящего номера, чтобы авторизоваться.',
    sms: 'На ваш номер сейчас поступит смс сообщение. Введите код из смс, чтобы авторизоваться.'
}

const DEBOUNCE_TIME = 800;
const PERSONAL_DATA_LINK = 'https://smartbe.ru/privacy';

const AutorizationModal = React.memo(({
    closeConfig, // Стилевой конфиг для кнопки закрытия
    className,
    wrapperClassName,
    timeToNextCall, // Время таймера до следующего звонка
    setTimeToNextCall,
}) => {
    /*eslint-disable no-unused-vars*/
    let nextCallInterval;
    const dispatch = useDispatch();
    const isAuthModalOpen = useSelector(getIsAuthModalOpened);
    const isPhoneAuthRequestFailed = useSelector(getIsPhoneAuthRequestFailed);
    const isCodeError = !useSelector(getIsAuthCodeValid);
    const codeErrorMessage = useSelector(getAuthCodeErrorMessage);

    const [isFirstStep, setIsFirstStep] = useState(true);
    const [phoneNumber, setPhoneNumber] = useState('');
    const [isIntervalStarted, setIntervalStarted] = useState(false);
    const [inputTimeout, setInputTimeout] = useState(undefined);
    const [isResendCode, setCodeResent] = useState(false);
    const [authType, setAuthType] = useState(SMS_AUTH_TYPE);
    const [isPhoneValid, setIsPhoneValid] = useState(false);
    const [captchaError, setCaptchaError] = useState(false);
    const [isCaptchaReady, setIsCaptchaReady] = useState(false);
    const [isCaptchaSolved, setIsCaptchaSolved] = useState(false);
    const recaptchaWidgetIdRef = useRef(null);
    const recaptchaContainerRef = useRef(null);
    const close = useCallback(() => dispatch(metaActions.toggleAuthModal(false)), [dispatch]);

    // Капча нужна только перед отправкой SMS: на 1-м шаге и при повторной отправке (таймер = 0).
    // На экране ввода кода, пока идёт таймер, капчу не показываем.
    const shouldShowCaptcha = isFirstStep || (!isFirstStep && timeToNextCall <= 0);

    const clearRecaptcha = useCallback(() => {
        resetRecaptchaWidget(recaptchaWidgetIdRef.current);
        recaptchaWidgetIdRef.current = null;
        setIsCaptchaReady(false);
        setIsCaptchaSolved(false);

        if (recaptchaContainerRef.current) {
            recaptchaContainerRef.current.innerHTML = '';
        }
    }, []);

    const checkPhoneValidity = (number, country) => {
        const isLengthValid = number.length >= COUNTRIES_PARAMS.PHONE_LENGTHS[country.countryCode]
        setIsPhoneValid(isLengthValid);
    };

    const handleCodeEntered = (code) => {
        const phone = PLUS_SIGN + phoneNumber;
        dispatch(metaActions.confirmPhoneAuth(phone, code));
    };

    function checkPhoneCode(input) {
        const checkEnteredCode = () => {
            handleCodeEntered(input);
        }

        clearInterval(inputTimeout);

        if (input.length === CODE_INPUT_FIELDS_AMOUNT) {
            // Установка таймаута для дебаунса при вводе кода
            setInputTimeout(setTimeout(checkEnteredCode, DEBOUNCE_TIME));
        }
    };

    const handleSendClicked = (type) => {
        const recaptchaResponse = getRecaptchaResponse(
            recaptchaWidgetIdRef.current,
            recaptchaContainerRef.current,
        );
        if (!recaptchaResponse) {
            setCaptchaError(true);
            setIsCaptchaSolved(false);
            return;
        }

        setCaptchaError(false);
        setAuthType(type);
        dispatch(metaActions.resetAuthCodeValidation());

        if (type === SMS_AUTH_TYPE) {
            capturePosthogEvent(posthogEvents.AUTH_SMS_REQUEST);
        } else {
            capturePosthogEvent(posthogEvents.AUTH_CALL_REQUEST);
        }

        const phone = PLUS_SIGN + phoneNumber;
        dispatch(metaActions.sendPhoneAuthRequest(phone, type, recaptchaResponse));
        resetRecaptchaWidget(recaptchaWidgetIdRef.current);
        setIsCaptchaSolved(false);
    };

    const resetCode = () => {
        setCodeResent(!isResendCode);
        setTimeToNextCall(DEFAULT_TIME_TO_NEXT_CALL);
        dispatch(metaActions.resetAuthCodeValidation());
    };

    const autorizeByTypeCTA = (type) => {
        if (type === SMS_AUTH_TYPE) {
            return <button
                className={s.autorizationLinkText}
                type="button"
                disabled={!isCaptchaSolved}
                onClick={() => {
                    if (!isCaptchaSolved) {
                        setCaptchaError(true);
                        return;
                    }
                    resetCode();
                    handleSendClicked(SMS_AUTH_TYPE);
                }}>Получить код повторно</button>;
        }

        return <button
            className={cx(s.autorizationLinkText, s.recallCta)}
            type="button"
            disabled={!isCaptchaSolved}
            onClick={() => {
                if (!isCaptchaSolved) {
                    setCaptchaError(true);
                    return;
                }
                resetCode();
                handleSendClicked(CALL_AUTH_TYPE);
            }
            }>Позвонить мне повторно</button>;
    };

    const changeAuthTypeButton = () => {
        return <button
            className={s.autorizationLinkText}
            type="button"
            onClick={() => {
                // setAuthType(SMS_AUTH_TYPE);
                resetCode();
                handleSendClicked(SMS_AUTH_TYPE);
            }}>Войти по СМС</button>;
    };

    const getNextStep = () => {
        if (!isCaptchaReady || !isCaptchaSolved) {
            setCaptchaError(true);
            return;
        }

        const recaptchaResponse = getRecaptchaResponse(
            recaptchaWidgetIdRef.current,
            recaptchaContainerRef.current,
        );
        if (!recaptchaResponse) {
            setCaptchaError(true);
            setIsCaptchaSolved(false);
            return;
        }

        setCaptchaError(false);
        setIsFirstStep(false);

        if (isPhoneValid) {
            if (!isIntervalStarted) {
                setIntervalStarted(true);
                nextCallInterval = setInterval(() => {
                    setTimeToNextCall((timeToNextCall) => timeToNextCall - 1);
                }, 1000);
            }

            handleSendClicked(SMS_AUTH_TYPE);
        }
    };

    const renderCaptchaBlock = () => (
        <div className={s.captchaBlock}>
            <div className={s.captchaWidget}>
                <div ref={recaptchaContainerRef} className={s.recaptchaContainer} />
            </div>
            {!isCaptchaReady && shouldShowCaptcha && (
                <div className={s.recaptchaLoading}>Загрузка капчи...</div>
            )}
            {captchaError && (
                <div className={s.captchaErrorMessage}>
                    {!isCaptchaReady
                        ? 'Подождите, загружается капча'
                        : 'Пройдите проверку, чтобы продолжить'}
                </div>
            )}
        </div>
    );

    const firstStep = (getNextStep, personalAgreementLink) => {
        return (
            <React.Fragment>
                <div className={s.phoneSection}>
                    <div className={s.modalText}>Телефон</div>
                    <PhoneInput
                        country={'ru'}
                        localization={COUNTRIES_PARAMS.LOCALIZATION}
                        onlyCountries={COUNTRIES_LIST}
                        preserveOrder={['onlyCountries', 'preferredCountries']}
                        countryCodeEditable={false}
                        containerClass={s.phoneInputContainer}
                        inputStyle={inputStyles}
                        disableCountryGuess={true}
                        dropdownStyle={dropdownStyles}
                        value={phoneNumber.toString()}
                        masks={COUNTRIES_PARAMS.PHONE_MASKS}
                        onChange={(phone, country) => {
                            setPhoneNumber(phone);
                            checkPhoneValidity(phone, country);
                        }}
                    />
                </div>
                <div className={s.continueSection}>
                    {renderCaptchaBlock()}
                    <Button
                        className={s.continueButton}
                        type="submit"
                        background="blue"
                        disabled={isPhoneValid === false || !isCaptchaReady || !isCaptchaSolved}
                        onClick={getNextStep}>
                        Продолжить
                    </Button>
                    <div className={s.autorizationModalFooterStepOne}>
                        Нажимая на кнопку, вы даете согласие на <a href={personalAgreementLink}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={s.modalFooterLink}>обработку своих персональных данных</a>
                    </div>
                </div>
            </React.Fragment>
        )
    };

    const getTimeoutDisplay = (timeToNextCall) => {
        let timeoutdisplay = `00:${timeToNextCall}`;

        if (timeToNextCall < 10) {
            timeoutdisplay = `00:0${timeToNextCall}`;
        }

        return timeoutdisplay;
    };

    const timeoutDisplay = getTimeoutDisplay(timeToNextCall);

    const secondStep = () => {
        const isSMSAuthTypeAvailable = isPhoneAuthRequestFailed || timeToNextCall < TIME_BEFORE_SMS_AVAILABLE;

        let invalidCodeError = null;
        if (isCodeError){
            invalidCodeError = !codeErrorMessage
                ? <div className={s.autorizationErrorMessage}>Неверный код. Попробуйте ещё раз</div>
                : <div className={s.autorizationErrorMessage}>{codeErrorMessage}</div>;
        }
       
        const phoneAuthRequestError = isPhoneAuthRequestFailed &&
            <div className={s.autorizationErrorMessage}>Не получилось отправить код,
                попробуйте обновить страницу и повторить через минуту</div>;

        const phoneCallAuthFooterContent =
            <>
                {timeToNextCall > 0
                    ? <div className={s.autorizationCallTimeout}>Позвонить повторно можно через {timeoutDisplay}</div>
                    : autorizeByTypeCTA(CALL_AUTH_TYPE)}
                {isSMSAuthTypeAvailable
                    ? changeAuthTypeButton()
                    : null}
            </>;

        const smsAuthFooterContent =
            <>
                {timeToNextCall > 0
                    ? <div className={s.autorizationCallTimeout}>Отправить код повторно можно через {timeoutDisplay}</div>
                    : (
                        <>
                            {renderCaptchaBlock()}
                            {autorizeByTypeCTA(SMS_AUTH_TYPE)}
                        </>
                    )}
            </>

        return (
            <React.Fragment>
                <div className={s.modalText}>
                    {modalDescriptionOptions.sms}
                </div>
                <CodeInput
                    key={`codeInput-${isResendCode}`}
                    className={s.codeInput}
                    name="code-validation"
                    type="text"
                    fields={CODE_INPUT_FIELDS_AMOUNT}
                    inputStyle={{ ...codeInputStyles, border: isCodeError ? '1px solid #FF6769' : '1px solid #e6e6e6' }}
                    onChange={input => checkPhoneCode(input)}
                    inputMode="numeric"
                />
                <div className={s.autorizationModalFooter}>
                    {isCodeError && !isPhoneAuthRequestFailed && invalidCodeError}
                    {isPhoneAuthRequestFailed && phoneAuthRequestError}
                    {/* {authType === CALL_AUTH_TYPE && phoneCallAuthFooterContent} */}
                    {authType === SMS_AUTH_TYPE && smsAuthFooterContent}
                    {/* {smsAuthFooterContent} */}
                </div>
            </React.Fragment>
        )
    };

    useLayoutEffect(() => {
        if (!isAuthModalOpen || !shouldShowCaptcha) {
            clearRecaptcha();
            return undefined;
        }

        let cancelled = false;
        let attempts = 0;

        const mountCaptcha = () => {
            if (cancelled) {
                return;
            }

            if (!recaptchaContainerRef.current) {
                if (attempts < 20) {
                    attempts += 1;
                    requestAnimationFrame(mountCaptcha);
                }
                return;
            }

            renderRecaptchaWidget(recaptchaContainerRef.current, RECAPTCHA_SITE_KEY, {
                callback: () => {
                    setIsCaptchaSolved(true);
                    setCaptchaError(false);
                },
                expiredCallback: () => {
                    setIsCaptchaSolved(false);
                },
                errorCallback: () => {
                    setIsCaptchaSolved(false);
                    setCaptchaError(true);
                },
            })
                .then((widgetId) => {
                    if (cancelled) {
                        return;
                    }

                    recaptchaWidgetIdRef.current = widgetId;
                    setIsCaptchaReady(widgetId !== null);
                    setIsCaptchaSolved(false);
                });
        };

        mountCaptcha();

        return () => {
            cancelled = true;
        };
    }, [isAuthModalOpen, shouldShowCaptcha, clearRecaptcha]);

    useEffect(() => {
        setTimeToNextCall(DEFAULT_TIME_TO_NEXT_CALL)
        if (!isAuthModalOpen) {
            setIsFirstStep(true);
            setPhoneNumber('')
            setIsPhoneValid(false);
            setCaptchaError(false);
            clearRecaptcha();
        }
    }, [isAuthModalOpen, isFirstStep, setTimeToNextCall, clearRecaptcha]);

    return (
        <>
            {isAuthModalOpen && <Modal
                className={cx(s.autorizationModal, className)}
                wrapperClassName={cx(s.autorizationModalWrapper, wrapperClassName)}
                overlayClassName={s.autorizationModalOverlay}
                closeConfig={{
                    ...closeConfig,
                    color: 'black',
                    width: 10,
                    height: 10,
                    className: s.closeButton,
                }}
                onClose={close}
                showClose
                isPortal
            >
                <div className={s.autorizationModalContent}>
                    <h2 className={cx(s.modalHeader)}>
                        {isFirstStep
                            ? modalHeaderTextOptions.firstStep
                            : modalHeaderTextOptions.secondStepSms
                            // : (authType === CALL_AUTH_TYPE
                            //     ? modalHeaderTextOptions.secondStepCall
                            //     : modalHeaderTextOptions.secondStepSms)
                        }
                    </h2>
                    {isFirstStep ? firstStep(getNextStep, PERSONAL_DATA_LINK) : secondStep(timeToNextCall.toString())}
                </div>
            </Modal>
            }
        </>
    );
});

export default AutorizationModal;